import React, { useState } from 'react';
import { Avatar, Menu, MenuItem, IconButton as IconButtonMui, Typography, Divider, Box, Button, ButtonGroup } from '@mui/material';
import { useOnlineStatus } from '../context/OnlineStatusContext';
import Icon from '../components/shared/Icon';
import { useAuth } from '../hooks/useAuth';
import { capitalizeFirstLetter, formatName } from '../utils/capitalize';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { logout } from '../services/apiSLTP/auth';
import IconButton from '../components/shared/IconButton';
import tinycolor from 'tinycolor2';
import { useTheme } from '../hooks/useTheme';

const ProfileMenu = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { isOnline } = useOnlineStatus();
    const { user, logout: removeConnection } = useAuth();
    const { t } = useTranslation();
    const { muiTheme } = useTheme();
    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        await logout();
        removeConnection();
        navigate('/login');
    };

    return (
        <>
            <IconButtonMui onClick={handleClick} size="small">
                <Avatar
                    alt="Profile"
                    sx={{
                        width: 30,
                        height: 30,
                        bgcolor: user?.color,
                        color: tinycolor(user?.color).isDark() ? muiTheme.palette.common.white : muiTheme.palette.common.black,
                    }}
                >
                    {!isOnline && <Icon name="wifiOff" color="disabled" />}
                </Avatar>
            </IconButtonMui>

            <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{  gap: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            minHeight: 48,
                        }}
                    >
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{
                                textAlign: 'center',
                                flexGrow: 1,
                            }}
                        >
                            @{user?.username}
                        </Typography>

                        <IconButton
                            iconName="close"
                            onClick={handleClose}
                            size="small"
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                            }}
                        ></IconButton>
                    </Box>

                    <Avatar
                        alt="Profile"
                        sx={{
                            width: 80,
                            height: 80,
                            bgcolor: user?.color,
                            color: tinycolor(user?.color).isDark() ? muiTheme.palette.common.white : muiTheme.palette.common.black,
                        }}
                    >
                        {!isOnline && <Icon name="wifiOff" color="disabled" />}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" textAlign="center">
                        {capitalizeFirstLetter(t('hello'))} {formatName(user?.firstName, user?.lastName)} !
                    </Typography>
                    <ButtonGroup variant="contained" color="inherit" size="large" sx={{ margin: 2 }}>
                        <Button
                            startIcon={<Icon name="person" />}
                            onClick={() => {
                                navigate('/profile');
                                handleClose();
                            }}
                        >
                            {capitalizeFirstLetter(t('myProfile'))}
                        </Button>
                        <Button
                            startIcon={<Icon name="logout" />}
                            onClick={() => {
                                handleLogout();
                                handleClose();
                            }}
                        >
                            {capitalizeFirstLetter(t('logout'))}
                        </Button>
                    </ButtonGroup>
                </Box>
            </Menu>
        </>
    );
};

export default ProfileMenu;
